import type { JobScopeChangeDialogViewModel } from '@/features/jobs/details/components/JobScopeChangeDialog/JobScopeChangeDialogViewModal'
import { observer } from 'mobx-react-lite'
import { useId } from 'react'
import { FormContainer } from '@/components/FormContainer'
import { RadioContainer } from '@/components/inputs/RadioContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { NewBillingMethodDialog } from '@/features/billing/profile/NewBillingMethodDialog'
import {
  JobScopeChangeApprovedDialog,
  JobScopeChangeDeclineDialog,
  JobScopeChangeDialog,
  JobScopeChangeItemRow,
  JobScopeChangeTable,
  SkeletonTable,
} from '@/features/jobs/details/components/JobScopeChangeDialog/index'

interface JobScopeChangeDialogContainerProps {
  vm: JobScopeChangeDialogViewModel
}

/**
 * The JobScopeChangeDialogContainer
 */
export const JobScopeChangeDialogContainer = observer(
  ({ vm }: JobScopeChangeDialogContainerProps) => {
    const formId = useId()
    const isSkeleton =
      vm.fetchPriceBreakdown.pending || vm.checkHasDefaultBillingMethod.pending

    return (
      <>
        <JobScopeChangeDialog
          amountDue={
            vm.isPricingVisible
              ? vm.jobScopeChangePriceBreakdown.amountDue
              : null
          }
          price={
            vm.isPricingVisible
              ? vm.jobScopeChangePriceBreakdown.newScopePrice
              : null
          }
          displayName={vm.jobScopeChange.createdBy.displayName}
          respondedAt={vm.jobScopeChangePriceBreakdown.respondedAt}
          isOpen={vm.jobScopeChangeModal.showing}
          status={vm.jobScopeChange.status}
          isAcceptPending={vm.acceptChanges.pending}
          isDeclinePending={vm.declineChanges.pending}
          isSkeleton={isSkeleton}
          isNewBillingMethodNeeded={vm.isNewBillingMethodNeeded}
          onClose={vm.close}
          onDecline={() => vm.decline()}
          onAccept={() => vm.acceptChanges()}
          onNewBillingMethod={() => vm.newBillingMethod()}
          testId="job-scope-change-dialog"
          acceptTestId="job-scope-change-accept-button"
          declineTestId="job-scope-change-decline-button"
          addPaymentMethodTestId="job-scope-change-add-payment-method-button"
          closeTestId="job-scope-change-close-button"
        >
          {isSkeleton ? (
            <SkeletonTable count={vm.jobScopeChange.items.length} />
          ) : (
            <JobScopeChangeTable withoutPrice={!vm.isPricingVisible}>
              {vm.jobScopeChangePriceBreakdown.items.map((x, idx) => (
                <JobScopeChangeItemRow
                  key={idx}
                  title={x.name}
                  description={x.description}
                  quantity={x.newQuantity - x.previousQuantity}
                  price={
                    vm.isPricingVisible ? x.newPrice - x.previousPrice : null
                  }
                />
              ))}
            </JobScopeChangeTable>
          )}
        </JobScopeChangeDialog>

        <JobScopeChangeDeclineDialog
          form={formId}
          isOpen={vm.declineModal.showing}
          isPending={vm.declineChanges.pending}
          onClose={() => vm.goBack()}
          testId="job-scope-change-decline-dialog"
          goBackTestId="job-scope-change-decline-go-back-button"
          declineChangesTestId="job-scope-change-decline-confirm-button"
        >
          <FormContainer
            id={formId}
            disabled={vm.declineChanges.pending}
            onSubmit={(e) => {
              e.preventDefault()
              void vm.declineChanges()
            }}
          >
            <RadioContainer
              items={Object.entries(vm.declineReasons)}
              getKey={(item) => item[0]}
              getLabel={(item) => item[1]}
              name="reason"
              model={vm}
              field="declineReason"
            />
            {vm.declineReason === 'Other' && (
              <TextAreaContainer
                label="Any additional feedback?"
                hint="This will be visible to everyone"
                name="feedback"
                model={vm}
                field="feedback"
                autoFocus
                rows={5}
              />
            )}
          </FormContainer>
        </JobScopeChangeDeclineDialog>

        <JobScopeChangeApprovedDialog
          isOpen={vm.approvedModal.showing}
          onClose={vm.close}
          testId="job-scope-change-approved-dialog"
          okTestId="job-scope-change-approved-ok-button"
        />

        <NewBillingMethodDialog
          vm={vm.newBillingMethodDialogVm}
          testId="new-billing-method-dialog"
          cancelButtonTestId="new-billing-method-dialog-cancel-button"
          submitButtonTestId="new-billing-method-dialog-submit-button"
        />
      </>
    )
  },
)
